/**
 * App builds of mobile projects (docs/design/mobile-runs.md "App builds"): APKs uploaded to a project and
 * installed on the device by the runner. Android only for now; iOS builds come with the iOS runner.
 */
import { z } from 'zod';
import type { Platform } from './testing.js';

/** Platforms that take uploaded builds, and the file extension each accepts. */
export const BUILD_EXTENSIONS: Partial<Record<Platform, string>> = { ANDROID: '.apk' };

export const buildsSupported = (platform: Platform): boolean => BUILD_EXTENSIONS[platform] !== undefined;

/** Default upload limit (API: BUILD_MAX_MB). */
export const DEFAULT_BUILD_MAX_MB = 300;

/** Upload query string: the file goes as the raw request body. */
export const buildUploadQuerySchema = z.object({
  fileName: z.string().trim().min(1).max(200),
  notes: z.string().trim().max(500).optional(),
});

/** Run start body: a build of the project; omitted or null: the project's latest build. */
export const runBuildSchema = z.object({ appBuildId: z.string().min(1).max(64).nullish() });

/** Object key of a build file in artifact storage. */
export const buildStorageKey = (projectId: string, objectId: string): string => `builds/${projectId}/${objectId}.apk`;

export interface AppBuildView {
  id: string;
  projectId: string;
  platform: Platform;
  fileName: string;
  sizeBytes: number;
  sha256: string;
  packageName: string | null;
  versionName: string | null;
  versionCode: number | null;
  notes: string | null;
  uploadedBy: { id: string; name: string };
  createdAt: string;
  /** Runs that used it (for "used by"). */
  runCount: number;
  /** The signed-in person may delete it (uploader or Project Admin). */
  canDelete: boolean;
}

/** How a build is named in pickers and reports: "2.4 (240)", else the file name. */
export function buildLabel(b: { versionName: string | null; versionCode: number | null; fileName: string }): string {
  if (b.versionName) return b.versionCode !== null ? `${b.versionName} (${b.versionCode})` : b.versionName;
  return b.versionCode !== null ? `Version ${b.versionCode}` : b.fileName;
}
